import { Button, Checkbox, message, Space, Table, Typography, Input, Modal } from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CompanyPageLink } from "../../../components/Link/Link";
import List from "../../../components/list/list";
import Loading from "../../../components/loading";
import { fetchCompanies } from "../../../firebase/company/get";
import { canAddCompany, designations, findSitesByCompanyId, getCurrentUserDesignation } from "../../../helper/attribute";
import { selectCompanies } from "../../../redux/features/appSlice";
import AddCompanyModal from "./add";

import { T1 } from "../../../appconfig/texts";
import SelectSearch from "../../../components/select/SearchSelect";
import { dateSorter, getColumnSearchProps, getDropdownFilterProps } from "../../../components/table/tableProps";
import { updateCompany } from "../../../firebase/company/update";
import { catcher } from "../../../firebase/util";
import { getPlanTitles } from "../../../helper/plans";
import { routePath } from "../../../static/routes.static";
import DateInput from "../../../components/input/date";
import { getCurrentDatePlusNDays } from "../../../helper/time";
import FormContext from "../../../contexts/FormContext";
import useSize from "../../../hooks/useSize";

// DateInputComponent for the modal
const DateInputComponent = ({ condition, info, title, value, setValue, is_consultant_page, isMobile }) => {
  if (!condition) return null;

  return (
    <div style={{ marginBottom: '15px' }}>
      <div>
        <FormContext.Provider
          value={{
            bgless: true,
            borderless: true,
          }}
        >
          <DateInput info={!isMobile && info} clear title={title} value={value} setValue={setValue} is_consultant_page={is_consultant_page} />
        </FormContext.Provider>
        <Button size="small" type="link" onClick={() => setValue(null)}>clear</Button>
      </div>
    </div>
  );
};

// Advanced Settings Modal Component
const AdvancedSettingsModal = ({ visible, onClose, company, handlers, is_consultant_page, isMobile }) => {
  return (
    <Modal
    animation={false}
    transitionName=""
    maskTransitionName=""
    key={JSON.stringify(company)}
      title="Advanced Settings"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
    >
      <div style={{ padding: '20px 0' }}>
        {/* Date Settings */}
        <div style={{ marginBottom: '30px' }}>
          <h4 style={{ marginBottom: '15px', borderBottom: '1px solid #f0f0f0', paddingBottom: '8px' }}>Date Settings</h4>
          
          <DateInputComponent
            condition={["ma", "pro"].includes(company.plan)}
            title="Input Expiry date"
            value={company.companyExpiry}
            setValue={(companyExpiry) => handlers.handleSetCompanyExpiryDate(company.id, companyExpiry)}
            is_consultant_page={is_consultant_page}
            isMobile={isMobile}
          />
          <DateInputComponent
            condition={company.plan === "demo"}
            info="If not present, there will be no expiry date."
            title="Expiry date"
            value={company.planExpiry}
            setValue={(planExpiry) => handlers.handleUpdateCompanyPlanExpiry(company.id, planExpiry)}
            is_consultant_page={is_consultant_page}
            isMobile={isMobile}
          />
          <DateInputComponent
            condition={["ma", "DC"].includes(company.plan)}
            info="If not present records will default to today's date."
            title="Default Date"
            value={company.defaultDateTo}
            setValue={(defaultDateTo) => handlers.handleUpdateCompanyDefaultDateTo(company.id, defaultDateTo)}
            is_consultant_page={is_consultant_page}
            isMobile={isMobile}
          />
        </div>

        {/* Offset Market Settings */}
        <div>
          <h4 style={{ marginBottom: '15px', borderBottom: '1px solid #f0f0f0', paddingBottom: '8px' }}>Offset Market Settings</h4>
          
          {/* Tier dropdown */}
          <div style={{ marginBottom: '15px' }}>
            <div style={{ marginBottom: 4, fontSize: '12px' }}>Tier</div>
            <FormContext.Provider
              value={{
                bgless: true,
                borderless: true,
              }}
            >
              <SelectSearch 
                fullWidth 
                nolabel 
                size="small"
                value={company.offset_market_tier || " "}
                options={[" ", "Tier 1", "Tier 2", "Tier 3"]}
                setValue={(offset_market_tier) => handlers.handleUpdateCompanyOffsetMarketTier(company.id, offset_market_tier)}
              />
            </FormContext.Provider>
          </div>

          {/* AM Initials text input */}
          <div style={{ marginBottom: '15px' }}>
            <div style={{ marginBottom: 4, fontSize: '12px' }}>AM Initials</div>
            <AmInitialsInput 
              companyId={company.id}
              initialValue={company.offset_market_am_initials || ""}
              onSave={handlers.handleUpdateCompanyOffsetMarketAmInitials}
            />
          </div>

          {/* First time offsetting dropdown */}
          <div>
            <div style={{ marginBottom: 4, fontSize: '12px' }}>First Time Offsetting</div>
            <FormContext.Provider
              value={{
                bgless: true,
                borderless: true,
              }}
            >
              <SelectSearch 
                fullWidth 
                nolabel 
                size="small"
                value={company.offset_market_first_time_offsetting || "No"}
                options={["No", "Yes"]}
                setValue={(offset_market_first_time_offsetting) => handlers.handleUpdateCompanyOffsetMarketFirstTimeOffsetting(company.id, offset_market_first_time_offsetting)}
              />
            </FormContext.Provider>
          </div>
        </div>
      </div>
    </Modal>
  );
};

// Component for AM Initials input with save/cancel buttons
const AmInitialsInput = ({ companyId, initialValue, onSave }) => {
  const [value, setValue] = useState(initialValue);

  // Update local state when initialValue changes
  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleSave = () => {
    onSave(companyId, value);
  };

  const handleCancel = () => {
    setValue(initialValue);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <FormContext.Provider
        value={{
          bgless: true,
          borderless: true,
        }}
      >
        <Input
          size="small"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Enter initials"
          style={{ width: '120px' }}
        />
      </FormContext.Provider>
      <Button size="small" type="primary" onClick={handleSave}>Save</Button>
    </div>
  );
};

export default function Companies({ is_consultant_page }) {
  //todo: improve security here in allowing consultants
  const { isMobile, isExtraSmall } = useSize();

  const companies = useSelector(selectCompanies);
  const [modals, setModals] = useState({ add: false, update: false, advancedSettings: false });
  const [selectedCompany, setSelectedCompany] = useState(null);
  const navigate = useNavigate();
  const setModal = (whichOne, status = new Date().getTime()) => {
    modals[whichOne] = status;
    setModals({ ...modals });
  };

  const openAdvancedSettings = (company) => {
    setSelectedCompany(company);
    setModal("advancedSettings", true);
  };

  const closeAdvancedSettings = () => {
    setModal("advancedSettings", false);
    setSelectedCompany(null);
  };
  const handleUpdateCompanyPlan = async (id, newPlan) => {
    if (!is_consultant_page && designations.super_admin !== getCurrentUserDesignation()) return message.error("Not allowed");
    catcher(async () => {
      const obj = { plan: newPlan };
      if (newPlan === "demo") {
        obj.planExpiry = getCurrentDatePlusNDays(7);
      }
      await updateCompany(id, obj);
    }, {});
  };
  const handleSetCompanyExpiryDate = async (id, companyExpiry) => {
    window.alert("Please reconfirm this action.");


    if (!is_consultant_page && designations.super_admin !== getCurrentUserDesignation()) return message.error("You are not allowed");


    
    await catcher(async () => {
      await updateCompany(id, { companyExpiry });
    }, {});
  };
  const handleUpdateCompanyPlanExpiry = async (id, newPlanExpiry) => {
    if (!is_consultant_page && designations.super_admin !== getCurrentUserDesignation()) return message.error("You are not allowed");
    await catcher(async () => {
      await updateCompany(id, { planExpiry: newPlanExpiry });
    }, {});
  };
  const handleUpdateCompanyDefaultDateTo = async (id, defaultDateTo) => {
    if (!is_consultant_page && designations.super_admin !== getCurrentUserDesignation()) return message.error("You are not allowed");
    await catcher(async () => {
      await updateCompany(id, { defaultDateTo });
    }, {});
  };
  const handleUpdateCompanyOffsetMarketTier = async (id, offset_market_tier) => {
    if (!is_consultant_page && designations.super_admin !== getCurrentUserDesignation()) return message.error("You are not allowed");
    await catcher(async () => {
      await updateCompany(id, { offset_market_tier });
    }, {});
  };
  const handleUpdateCompanyOffsetMarketAmInitials = async (id, offset_market_am_initials) => {
    if (!is_consultant_page && designations.super_admin !== getCurrentUserDesignation()) return message.error("You are not allowed");
    await catcher(async () => {
      await updateCompany(id, { offset_market_am_initials });
    }, {});
  };
  const handleUpdateCompanyOffsetMarketFirstTimeOffsetting = async (id, offset_market_first_time_offsetting) => {
    if (!is_consultant_page && designations.super_admin !== getCurrentUserDesignation()) return message.error("You are not allowed");
    await catcher(async () => {
      await updateCompany(id, { offset_market_first_time_offsetting });
    }, {});
  };
  useEffect(() => {
    if (!companies) fetchCompanies();
  }, []);
  const columns = [
    {
      title: <T1 pos="companies.table.name" />,
      key: "name",
      render: (_) => (
        <>
          <CompanyPageLink main={true} id={_.id} />
        </>
      ),
      width: 200,
      ...getColumnSearchProps("name", (_) => _.title),
    },

    {
      title: <T1 pos="companies.table.sites" />,
      key: "sites",
      render: (_) => (
        <>
          <List
            translate="no"
            arr={findSitesByCompanyId(_.id).map((e) => e.title)}
            handleClick={(companyTitle) => {
              const site = findSitesByCompanyId(_.id)?.find((c) => c?.title === companyTitle);
              navigate(routePath.SiteDetails(site?.id));
            }}
            max={3}
          ></List>
        </>
      ),
      width: 200,
    },
    {
      title: <T1 pos="companies.table.createdAt" />,
      key: "createdAt",
      render: (_) => (
        <p style={{ fontWeight: 400 }}>
          <span>{_.createdAt}</span>
        </p>
      ),
      width: 200,
      sorter: dateSorter("createdAt"),
    },
    {
      title: <T1 pos="companies.table.startingMonth" />,
      key: "createdAt",
      render: (_) => (
        <p style={{ fontWeight: 400 }}>
          <span>{_.reporting_year_starting_month}</span>
        </p>
      ),
      width: 200,
    },
  ];
  if (is_consultant_page || designations.super_admin === getCurrentUserDesignation()) {
    columns.push({
      title: "Plan",
      key: "plan",
      render: (_) => {
        return (
          <>
            <SelectSearch fullWidth nolabel value={_.plan} options={getPlanTitles()} setValue={(plan) => handleUpdateCompanyPlan(_.id, plan)} />
          </>
        );
      },
      width: 200,
      ...getDropdownFilterProps(
        getPlanTitles().map((plan) => ({ plan })),
        "plan"
      ),
    });
    if (!isMobile) {
      columns.push({
        title: "More",
        key: "more",
        width: 150,
        render: (_) => {
          return (
            <Button 
              size="small" 
              onClick={() => openAdvancedSettings(_)}
            >
              Open Settings
            </Button>
          );
        },
      });
      if (!is_consultant_page)
        columns.push({
          title: "ID",
          key: "id",
          render: (_) => <>{_.id}</>,
          width: 200,
        });
    }
  }
  // Create handlers object for the modal
  const handlers = {
    handleSetCompanyExpiryDate,
    handleUpdateCompanyPlanExpiry,
    handleUpdateCompanyDefaultDateTo,
    handleUpdateCompanyOffsetMarketTier,
    handleUpdateCompanyOffsetMarketAmInitials,
    handleUpdateCompanyOffsetMarketFirstTimeOffsetting
  };

  if (!companies) return <Loading />;
  return (
    <>
      <Space>
        <Typography.Title level={isExtraSmall ? 3 : 2}>{<T1 pos="companies.title" />}</Typography.Title>
        {canAddCompany() && <Button onClick={() => setModal("add")}>{<T1 pos="companies.addButton" />}</Button>}
      </Space>
      {/*Table */}
      <Table scroll={{ y: "calc(100vh-220px)", x: 100 }} dataSource={companies} columns={columns} rowKey={"id"} pagination={true} />
      {/*Modals*/}
      <AddCompanyModal key={modals.add} visible={modals.add} setVisible={(v) => setModal("add", v)} />
      {selectedCompany && (
        <AdvancedSettingsModal
          visible={modals.advancedSettings}
          onClose={closeAdvancedSettings}
          company={companies.find(c => c.id === selectedCompany.id) || selectedCompany}
          handlers={handlers}
          is_consultant_page={is_consultant_page}
          isMobile={isMobile}
        />
      )}
    </>
  );
}
